import { PersistedQuestion, QuestionType } from '../questionTypes'

// The answers a respondent has entered so far, keyed by question id.
export type SurveyResponses = Record<number, string>

// Question types whose answers are persisted on a debounce (free typing) rather than
// saved immediately (discrete picks like a star or radio). Add a type here when it
// becomes a free-entry input — that's the only change needed to debounce its saves.
export const DEBOUNCED_SAVE_TYPES = new Set<QuestionType>(['free_form', 'dollar_value'])

export const isDebouncedSaveType = (type: QuestionType): boolean => DEBOUNCED_SAVE_TYPES.has(type)

// Camelcase domain shape for answers used throughout component-land.
export interface SurveyAnswer {
  questionId: number
  answer: string | null
}

export const fromSurveyAnswers = (answers: SurveyAnswer[]): SurveyResponses =>
  answers.reduce<SurveyResponses>(
    (acc, { questionId, answer }) => ({ ...acc, [questionId]: answer ?? '' }),
    {}
  )

export const toSurveyAnswers = (responses: SurveyResponses): SurveyAnswer[] =>
  Object.entries(responses).map(([questionId, answer]) => ({
    questionId: Number(questionId),
    answer
  }))

// The validation SIGNAL (not policy): which required questions are still empty.
// The caller decides when to check this and what to do about it.
export const findMissingRequiredIds = (questions: PersistedQuestion[], answers: SurveyResponses): number[] =>
  questions
    .filter((question) => question.required)
    .filter((question) => {
      const answer = answers[question.id]
      return answer == null || answer.trim() === ''
    })
    .map((question) => question.id)
